import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRestartOptions, humanError, restartTest, RestartOption } from '../api';
import { useFocusOnShow } from '../hooks';

interface Props {
  testId: number;
  testTitle: string;
  onClose: () => void;
  onDone: (name: string) => Promise<void>;
}

const utcDate = (value: string): Date => new Date(/(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`);
const moscowInput = (value: string | null): string => {
  if (!value || utcDate(value).getTime() <= Date.now()) return '';
  return new Date(utcDate(value).getTime() + 3 * 60 * 60 * 1000).toISOString().slice(0, 16);
};

export const RestartTestPanel: React.FC<Props> = ({ testId, testTitle, onClose, onDone }) => {
  const heading = useFocusOnShow<HTMLHeadingElement>();
  const [rows, setRows] = useState<RestartOption[]>([]);
  const [selected, setSelected] = useState('');
  const [due, setDue] = useState('');
  const [reason, setReason] = useState('Технический сбой при прохождении теста');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false);
  const requestRef = useRef(0);
  const row = rows.find((item) => String(item.assignment_id) === selected);
  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true); setError(''); setConfirmed(false); setSelected('');
    try {
      const items = await fetchRestartOptions(testId);
      if (request !== requestRef.current) return;
      setRows(items);
    } catch (failure) {
      if (request === requestRef.current) setError(humanError(failure, 'Не получилось загрузить сотрудников. Обновите список.'));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [testId]);
  useEffect(() => { void load(); return () => { requestRef.current += 1; }; }, [load]);
  const dueISO = due ? new Date(`${due}:00+03:00`) : null;
  const badDue = !!dueISO && (!Number.isFinite(dueISO.getTime()) || dueISO.getTime() <= Date.now());

  const submit = async () => {
    if (!row || !confirmed || badDue || reason.trim().length < 3 || busyRef.current) return;
    busyRef.current = true; setBusy(true); setError('');
    try {
      const result = await restartTest(testId, {
        assignment_id: row.assignment_id, expected_assigned_at: row.assigned_at,
        expected_attempt_id: row.attempt_id, expected_attempt_completed: row.attempt_completed,
        due_at: dueISO?.toISOString() ?? null, reason: reason.trim(),
      });
      await onDone(result.user_name);
    } catch (failure) {
      setConfirmed(false);
      setError(humanError(failure, 'Не удалось подтвердить перезапуск. Обновите список и проверьте состояние перед повтором.'));
    } finally { busyRef.current = false; setBusy(false); }
  };

  return (
    <div className="tw-card tw-stack-sm" aria-busy={busy || loading}>
      <h2 ref={heading} tabIndex={-1}>Перезапуск после технического сбоя</h2>
      <p className="tw-help">Выберите сотрудника для теста «{testTitle}». Прежняя попытка будет исключена из оценок и статистики как техническая. Ответы сохранятся в истории. Сотрудник начнёт заново с полным временем и получит письмо о повторном назначении.</p>
      {loading && <p className="tw-help">Загружаем сотрудников…</p>}
      {!loading && rows.length === 0 && !error && <p className="tw-help">Нет сотрудников с начатой или завершённой попыткой в текущей выдаче. Повторный перезапуск доступен после начала новой попытки.</p>}
      {!loading && rows.length > 0 && <>
        <label className="tw-stack-sm">Сотрудник
          <select className="tw-field" value={selected} disabled={busy} onChange={(event) => {
            const item = rows.find((candidate) => String(candidate.assignment_id) === event.target.value);
            setSelected(event.target.value); setDue(moscowInput(item?.due_at ?? null)); setConfirmed(false);
          }}>
            <option value="">Выберите сотрудника</option>
            {rows.map((item) => <option key={item.assignment_id} value={item.assignment_id}>{item.user_name}</option>)}
          </select>
        </label>
        {row && <>
          <p className="tw-help">{row.attempt_completed ? `Предыдущая попытка завершена${row.score == null ? '' : `: ${Math.round(row.score)}%`}.` : 'Предыдущая попытка ещё не завершена. Она будет закрыта по сохранённым ответам.'}</p>
          <label className="tw-stack-sm">Новый срок прохождения, МСК (необязательно)
            <input type="datetime-local" className="tw-field" value={due} disabled={busy} onChange={(event) => { setDue(event.target.value); setConfirmed(false); }} />
          </label>
          <p className="tw-help">Пустой срок — прохождение без ограничения по дате. Таймер самого теста начнётся при запуске сотрудником.</p>
          {badDue && <p role="alert">Укажите будущую дату и время по Москве.</p>}
          <label className="tw-stack-sm">Причина перезапуска
            <input className="tw-field" value={reason} maxLength={500} disabled={busy} onChange={(event) => { setReason(event.target.value); setConfirmed(false); }} />
          </label>
          <label className="tw-check">
            <input type="checkbox" checked={confirmed} disabled={busy} onChange={(event) => setConfirmed(event.target.checked)} />
            Подтверждаю перезапуск: {row.user_name}. Прежняя попытка не будет учитываться.
          </label>
        </>}
      </>}
      {error && <p role="alert">{error}</p>}
      <div className="tw-row tw-btn-row">
        <button type="button" className="tw-btn tw-btn-primary" disabled={!row || !confirmed || busy || loading || badDue || reason.trim().length < 3} onClick={() => void submit()}>{busy ? 'Перезапускаем…' : 'Перезапустить тест'}</button>
        <button type="button" className="tw-btn" disabled={busy || loading} onClick={() => void load()}>Обновить список</button>
        <button type="button" className="tw-btn" disabled={busy} onClick={onClose}>Отмена</button>
      </div>
    </div>
  );
};
